import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Typography,
  Space,
  Tag,
  Row,
  Col,
  Statistic,
  Popconfirm,
  Timeline,
  Rate,
  DatePicker
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  MessageOutlined,
  BarChartOutlined,
  FileExcelOutlined
} from '@ant-design/icons';
import { stationOwnerAPI } from '../services/api';
import { handleExport } from '../utils/exportUtils';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface Complaint {
  _id: string;
  complaintNumber: string;
  subject: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  station: {
    _id: string;
    name: string;
  };
  customer?: {
    _id: string;
    firstName: string;
    lastName: string;
    phone: string;
  };
  customerInfo?: {
    name: string;
    phone: string;
    email: string;
  };
  complaintDate: string;
  resolution?: {
    description: string;
    resolvedAt: string;
  };
  notes?: Array<{
    note: string;
    addedAt: string;
  }>;
  customerSatisfaction?: {
    rating: number;
    feedback: string;
  };
}

const Complaints: React.FC = () => {
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [stations, setStations] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [resolveModalVisible, setResolveModalVisible] = useState(false);
  const [noteModalVisible, setNoteModalVisible] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState<Complaint | null>(null);
  const [stats, setStats] = useState<any>(null);
  const [filters, setFilters] = useState<any>({
    stationId: 'all',
    status: 'all',
    priority: 'all',
    category: 'all',
    search: ''
  });
  const [form] = Form.useForm();
  const [resolveForm] = Form.useForm();
  const [noteForm] = Form.useForm();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchStations();
    fetchCustomers();
    fetchComplaints();
    fetchStats();
  }, [filters]);

  const fetchStations = async () => {
    try {
      const response = await stationOwnerAPI.getStations();
      setStations(response);
    } catch (error) {
      message.error('İstasyonlar yüklenemedi');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await stationOwnerAPI.getCustomers();
      setCustomers(response);
    } catch (error) {
      // Müşteriler opsiyonel
    }
  };

  const fetchComplaints = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (filters.stationId !== 'all') params.stationId = filters.stationId;
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.priority !== 'all') params.priority = filters.priority;
      if (filters.category !== 'all') params.category = filters.category;
      if (filters.search) params.search = filters.search;

      const response = await stationOwnerAPI.getComplaints(params);
      setComplaints(response);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Şikayetler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const params: any = {};
      if (filters.stationId !== 'all') params.stationId = filters.stationId;

      const response = await stationOwnerAPI.getComplaintStats(params);
      setStats(response);
    } catch (error) {
      // İstatistikler opsiyonel
    }
  };

  const handleAddComplaint = () => {
    setSelectedComplaint(null);
    form.resetFields();
    form.setFieldsValue({
      priority: 'medium',
      category: 'other',
      status: 'pending',
      complaintDate: dayjs()
    });
    setModalVisible(true);
  };

  const handleEditComplaint = (complaint: Complaint) => {
    setSelectedComplaint(complaint);
    form.setFieldsValue({
      ...complaint,
      stationId: complaint.station._id,
      customerId: complaint.customer?._id,
      complaintDate: complaint.complaintDate ? dayjs(complaint.complaintDate) : dayjs()
    });
    setModalVisible(true);
  };

  const handleViewDetail = async (complaint: Complaint) => {
    try {
      const response = await stationOwnerAPI.getComplaintDetail(complaint._id);
      setSelectedComplaint(response);
      setDetailModalVisible(true);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Şikayet detayı yüklenemedi');
    }
  };

  const handleSubmitComplaint = async (values: any) => {
    try {
      const data = {
        stationId: values.stationId,
        customerId: values.customerId,
        customerInfo: values.customerId ? undefined : {
          name: values.customerName,
          phone: values.customerPhone,
          email: values.customerEmail
        },
        subject: values.subject,
        description: values.description,
        category: values.category,
        priority: values.priority,
        status: values.status,
        complaintDate: values.complaintDate?.toISOString()
      };

      if (selectedComplaint) {
        await stationOwnerAPI.updateComplaint(selectedComplaint._id, data);
        message.success('Şikayet güncellendi');
      } else {
        await stationOwnerAPI.createComplaint(data);
        message.success('Şikayet oluşturuldu');
      }
      setModalVisible(false);
      form.resetFields();
      setSelectedComplaint(null);
      fetchComplaints();
      fetchStats();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const handleDeleteComplaint = async (id: string) => {
    try {
      await stationOwnerAPI.deleteComplaint(id);
      message.success('Şikayet silindi');
      fetchComplaints();
      fetchStats();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const handleResolve = (complaint: Complaint) => {
    setSelectedComplaint(complaint);
    resolveForm.resetFields();
    setResolveModalVisible(true);
  };

  const handleSubmitResolution = async (values: any) => {
    try {
      await stationOwnerAPI.resolveComplaint(selectedComplaint!._id, values.resolution);
      message.success('Şikayet çözüldü olarak işaretlendi');
      setResolveModalVisible(false);
      resolveForm.resetFields();
      setSelectedComplaint(null);
      fetchComplaints();
      fetchStats();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const handleAddNote = (complaint: Complaint) => {
    setSelectedComplaint(complaint);
    noteForm.resetFields();
    setNoteModalVisible(true);
  };

  const handleSubmitNote = async (values: any) => {
    try {
      await stationOwnerAPI.addComplaintNote(selectedComplaint!._id, values.note);
      message.success('Not eklendi');
      setNoteModalVisible(false);
      noteForm.resetFields();
      if (detailModalVisible) {
        handleViewDetail(selectedComplaint!);
      } else {
        fetchComplaints();
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const columns = [
    {
      title: 'Şikayet No',
      dataIndex: 'complaintNumber',
      key: 'complaintNumber',
      render: (text: string) => <span style={{ fontWeight: 'bold' }}>{text}</span>
    },
    {
      title: 'Konu',
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true
    },
    {
      title: 'İstasyon',
      dataIndex: ['station', 'name'],
      key: 'station'
    },
    {
      title: 'Müşteri',
      key: 'customer',
      render: (record: Complaint) => {
        if (record.customer) {
          return `${record.customer.firstName} ${record.customer.lastName}`;
        }
        return record.customerInfo?.name || 'Bilinmeyen';
      }
    },
    {
      title: 'Kategori',
      dataIndex: 'category',
      key: 'category',
      render: (category: string) => {
        const colors: any = {
          service: 'blue',
          product: 'green',
          price: 'orange',
          staff: 'purple',
          cleanliness: 'cyan',
          safety: 'red',
          other: 'default'
        };
        const labels: any = {
          service: 'Hizmet',
          product: 'Ürün',
          price: 'Fiyat',
          staff: 'Personel',
          cleanliness: 'Temizlik',
          safety: 'Güvenlik',
          other: 'Diğer'
        };
        return <Tag color={colors[category]}>{labels[category] || category}</Tag>;
      }
    },
    {
      title: 'Öncelik',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority: string) => {
        const colors: any = {
          low: 'default',
          medium: 'processing',
          high: 'warning',
          urgent: 'error'
        };
        const labels: any = {
          low: 'Düşük',
          medium: 'Orta',
          high: 'Yüksek',
          urgent: 'Acil'
        };
        return <Tag color={colors[priority]}>{labels[priority] || priority}</Tag>;
      }
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const colors: any = {
          pending: 'default',
          in_progress: 'processing',
          resolved: 'success',
          closed: 'success',
          rejected: 'error'
        };
        const labels: any = {
          pending: 'Beklemede',
          in_progress: 'İşleniyor',
          resolved: 'Çözüldü',
          closed: 'Kapatıldı',
          rejected: 'Reddedildi'
        };
        const icons: any = {
          pending: <ClockCircleOutlined />,
          in_progress: <ExclamationCircleOutlined />,
          resolved: <CheckCircleOutlined />,
          closed: <CheckCircleOutlined />,
          rejected: <CloseCircleOutlined />
        };
        return (
          <Tag color={colors[status]} icon={icons[status]}>
            {labels[status] || status}
          </Tag>
        );
      }
    },
    {
      title: 'Tarih',
      dataIndex: 'complaintDate',
      key: 'complaintDate',
      render: (date: string) => dayjs(date).format('DD MMM YYYY')
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: Complaint) => (
        <Space>
          <Button
            type="link"
            icon={<MessageOutlined />}
            onClick={() => handleViewDetail(record)}
          />
          {record.status !== 'resolved' && record.status !== 'closed' && (
            <>
              <Button
                type="link"
                icon={<CheckCircleOutlined />}
                onClick={() => handleResolve(record)}
                title="Çöz"
              />
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => handleEditComplaint(record)}
              />
            </>
          )}
          <Popconfirm
            title="Bu şikayeti silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDeleteComplaint(record._id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} md={12}>
          <Title level={2} style={{ margin: 0, fontSize: isMobile ? '20px' : '24px' }}>
            <ExclamationCircleOutlined /> Müşteri Şikayetleri
          </Title>
        </Col>
        <Col xs={24} md={12}>
          <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: isMobile ? '100%' : 'auto' }} size="small">
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={handleAddComplaint} 
              block={isMobile}
            >
              Yeni Şikayet Ekle
            </Button>
            <Button 
              icon={<BarChartOutlined />} 
              onClick={fetchStats} 
              block={isMobile}
            >
              İstatistikler
            </Button>
            <Button 
              icon={<FileExcelOutlined />}
              onClick={() => handleExport(
                () => stationOwnerAPI.exportComplaintsExcel(),
                `sikayetler_${Date.now()}.xlsx`,
                () => message.success('Excel başarıyla indirildi')
              )}
              block={isMobile}
            >
              Excel İndir
            </Button>
          </Space>
        </Col>
      </Row>

      {/* İstatistikler */}
      {stats && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Toplam Şikayet"
                value={stats.total}
                prefix={<ExclamationCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Beklemede"
                value={stats.pending}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Çözüldü"
                value={stats.resolved}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Çözüm Oranı"
                value={stats.resolutionRate}
                precision={1}
                suffix="%"
                prefix={<BarChartOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Filtreler */}
      <Card style={{ marginBottom: 24 }} styles={{ body: { padding: isMobile ? '12px' : '16px' } }}>
        <Row gutter={[8, 8]}>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Ara (şikayet no, konu, müşteri)"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              allowClear
              size={isMobile ? 'small' : 'middle'}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              value={filters.stationId}
              onChange={(value) => setFilters({ ...filters, stationId: value })}
              style={{ width: '100%' }}
              placeholder="İstasyon"
              size={isMobile ? 'small' : 'middle'}
            >
              <Option value="all">Tüm İstasyonlar</Option>
              {stations.map((station: any) => (
                <Option key={station._id} value={station._id}>
                  {station.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              value={filters.status}
              onChange={(value) => setFilters({ ...filters, status: value })}
              style={{ width: '100%' }}
              placeholder="Durum"
              size={isMobile ? 'small' : 'middle'}
            >
              <Option value="all">Tümü</Option>
              <Option value="pending">Beklemede</Option>
              <Option value="in_progress">İşleniyor</Option>
              <Option value="resolved">Çözüldü</Option>
              <Option value="closed">Kapatıldı</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              value={filters.priority}
              onChange={(value) => setFilters({ ...filters, priority: value })}
              style={{ width: '100%' }}
              placeholder="Öncelik"
              size={isMobile ? 'small' : 'middle'}
            >
              <Option value="all">Tümü</Option>
              <Option value="low">Düşük</Option>
              <Option value="medium">Orta</Option>
              <Option value="high">Yüksek</Option>
              <Option value="urgent">Acil</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              value={filters.category}
              onChange={(value) => setFilters({ ...filters, category: value })}
              style={{ width: '100%' }}
              placeholder="Kategori"
              size={isMobile ? 'small' : 'middle'}
            >
              <Option value="all">Tümü</Option>
              <Option value="service">Hizmet</Option>
              <Option value="product">Ürün</Option>
              <Option value="price">Fiyat</Option>
              <Option value="staff">Personel</Option>
              <Option value="cleanliness">Temizlik</Option>
              <Option value="safety">Güvenlik</Option>
              <Option value="other">Diğer</Option>
            </Select>
          </Col>
        </Row>
      </Card>

      <Card styles={{ body: { padding: isMobile ? '8px' : '24px' } }}>
        <Table
          columns={columns}
          dataSource={complaints}
          rowKey="_id"
          loading={loading}
          pagination={{ 
            pageSize: 10,
            showSizeChanger: false,
            responsive: true
          }}
          scroll={{ x: 'max-content', y: isMobile ? '60vh' : undefined }}
          size={isMobile ? 'small' : 'middle'}
          onRow={(record) => ({
            onClick: () => handleViewDetail(record),
            style: { cursor: 'pointer' }
          })}
        />
      </Card>

      {/* Şikayet Modal */}
      <Modal
        title={selectedComplaint ? 'Şikayet Düzenle' : 'Yeni Şikayet Ekle'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setSelectedComplaint(null);
          form.resetFields();
        }}
        footer={null}
        width={isMobile ? '95%' : 800}
        styles={{ body: { padding: isMobile ? '16px' : '24px', maxHeight: '80vh', overflowY: 'auto' } }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmitComplaint}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="stationId"
                label="İstasyon"
                rules={[{ required: true, message: 'İstasyon seçiniz' }]}
              >
                <Select placeholder="İstasyon seçiniz">
                  {stations.map((station: any) => (
                    <Option key={station._id} value={station._id}>
                      {station.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="customerId"
                label="Müşteri (Opsiyonel)"
              >
                <Select placeholder="Müşteri seçiniz" allowClear showSearch>
                  {customers.map((customer: any) => (
                    <Option key={customer._id} value={customer._id}>
                      {customer.firstName} {customer.lastName} - {customer.phone}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="customerName"
                label="Müşteri Adı (Müşteri seçilmediyse)"
                dependencies={['customerId']}
              >
                <Input placeholder="Müşteri adı" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="customerPhone"
                label="Telefon"
                dependencies={['customerId']}
              >
                <Input placeholder="Telefon" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="customerEmail"
                label="Email"
                dependencies={['customerId']}
              >
                <Input placeholder="Email" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="subject"
                label="Konu"
                rules={[{ required: true, message: 'Konu gereklidir' }]}
              >
                <Input placeholder="Şikayet konusu" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="complaintDate"
                label="Şikayet Tarihi"
                rules={[{ required: true }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="category"
                label="Kategori"
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value="service">Hizmet</Option>
                  <Option value="product">Ürün</Option>
                  <Option value="price">Fiyat</Option>
                  <Option value="staff">Personel</Option>
                  <Option value="cleanliness">Temizlik</Option>
                  <Option value="safety">Güvenlik</Option>
                  <Option value="other">Diğer</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="priority"
                label="Öncelik"
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value="low">Düşük</Option>
                  <Option value="medium">Orta</Option>
                  <Option value="high">Yüksek</Option>
                  <Option value="urgent">Acil</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="status"
                label="Durum"
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value="pending">Beklemede</Option>
                  <Option value="in_progress">İşleniyor</Option>
                  <Option value="resolved">Çözüldü</Option>
                  <Option value="closed">Kapatıldı</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name="description"
                label="Açıklama"
                rules={[{ required: true, message: 'Açıklama gereklidir' }]}
              >
                <TextArea rows={5} placeholder="Şikayet detayları" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {selectedComplaint ? 'Güncelle' : 'Oluştur'}
              </Button>
              <Button onClick={() => {
                setModalVisible(false);
                setSelectedComplaint(null);
                form.resetFields();
              }}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Şikayet Detay Modal */}
      <Modal
        title={`Şikayet Detayı - ${selectedComplaint?.complaintNumber}`}
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedComplaint(null);
        }}
        footer={null}
        width={isMobile ? '95%' : 900}
        styles={{ body: { padding: isMobile ? '16px' : '24px', maxHeight: '80vh', overflowY: 'auto' } }}
      >
        {selectedComplaint && (
          <div>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12}>
                <Card size="small">
                  <Statistic
                    title="Durum"
                    value={selectedComplaint.status}
                    prefix={
                      selectedComplaint.status === 'resolved' || selectedComplaint.status === 'closed' 
                        ? <CheckCircleOutlined style={{ color: '#3f8600' }} />
                        : <ClockCircleOutlined style={{ color: '#faad14' }} />
                    }
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12}>
                <Card size="small">
                  <Statistic
                    title="Öncelik"
                    value={selectedComplaint.priority}
                  />
                </Card>
              </Col>
            </Row>

            <Card title="Şikayet Bilgileri" style={{ marginBottom: 16 }}>
              <p><strong>Konu:</strong> {selectedComplaint.subject}</p>
              <p><strong>Açıklama:</strong> {selectedComplaint.description}</p>
              <p><strong>İstasyon:</strong> {selectedComplaint.station.name}</p>
              <p><strong>Tarih:</strong> {dayjs(selectedComplaint.complaintDate).format('DD MMM YYYY HH:mm')}</p>
            </Card>

            {selectedComplaint.resolution && (
              <Card title="Çözüm" style={{ marginBottom: 16 }}>
                <p>{selectedComplaint.resolution.description}</p>
                <p style={{ fontSize: '12px', color: '#666' }}>
                  Çözüldü: {dayjs(selectedComplaint.resolution.resolvedAt).format('DD MMM YYYY HH:mm')}
                </p>
              </Card>
            )}

            {selectedComplaint.notes && selectedComplaint.notes.length > 0 && (
              <Card title="Notlar" style={{ marginBottom: 16 }}>
                <Timeline>
                  {selectedComplaint.notes.map((note: any, index: number) => (
                    <Timeline.Item key={index}>
                      <p>{note.note}</p>
                      <p style={{ fontSize: '12px', color: '#666' }}>
                        {dayjs(note.addedAt).format('DD MMM YYYY HH:mm')}
                      </p>
                    </Timeline.Item>
                  ))}
                </Timeline>
              </Card>
            )}

            {selectedComplaint.customerSatisfaction && (
              <Card title="Müşteri Memnuniyeti" style={{ marginBottom: 16 }}>
                <Rate disabled value={selectedComplaint.customerSatisfaction.rating} />
                {selectedComplaint.customerSatisfaction.feedback && (
                  <p style={{ marginTop: 8 }}>{selectedComplaint.customerSatisfaction.feedback}</p>
                )}
              </Card>
            )}

            <Space>
              {selectedComplaint.status !== 'resolved' && selectedComplaint.status !== 'closed' && (
                <>
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={() => handleResolve(selectedComplaint)}
                  >
                    Çöz
                  </Button>
                  <Button
                    icon={<MessageOutlined />}
                    onClick={() => handleAddNote(selectedComplaint)}
                  >
                    Not Ekle
                  </Button>
                </>
              )}
              <Button onClick={() => setDetailModalVisible(false)}>
                Kapat
              </Button>
            </Space>
          </div>
        )}
      </Modal>

      {/* Çözüm Modal */}
      <Modal
        title="Şikayeti Çöz"
        open={resolveModalVisible}
        onCancel={() => {
          setResolveModalVisible(false);
          resolveForm.resetFields();
        }}
        footer={null}
        width={isMobile ? '95%' : 600}
        styles={{ body: { padding: isMobile ? '16px' : '24px', maxHeight: '80vh', overflowY: 'auto' } }}
      >
        <Form
          form={resolveForm}
          layout="vertical"
          onFinish={handleSubmitResolution}
        >
          <Form.Item
            name="resolution"
            label="Çözüm Açıklaması"
            rules={[{ required: true, message: 'Çözüm açıklaması gereklidir' }]}
          >
            <TextArea rows={5} placeholder="Şikayetin nasıl çözüldüğünü açıklayın" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Çözüldü Olarak İşaretle
              </Button>
              <Button onClick={() => {
                setResolveModalVisible(false);
                resolveForm.resetFields();
              }}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Not Ekle Modal */}
      <Modal
        title="Not Ekle"
        open={noteModalVisible}
        onCancel={() => {
          setNoteModalVisible(false);
          noteForm.resetFields();
        }}
        footer={null}
        width={isMobile ? '95%' : 600}
        styles={{ body: { padding: isMobile ? '16px' : '24px', maxHeight: '80vh', overflowY: 'auto' } }}
      >
        <Form
          form={noteForm}
          layout="vertical"
          onFinish={handleSubmitNote}
        >
          <Form.Item
            name="note"
            label="Not"
            rules={[{ required: true, message: 'Not gereklidir' }]}
          >
            <TextArea rows={4} placeholder="Notunuzu yazın" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Ekle
              </Button>
              <Button onClick={() => {
                setNoteModalVisible(false);
                noteForm.resetFields();
              }}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Complaints;

